/**********************************************************************
 * Copyright (C) 2023-2025 Red Hat, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * SPDX-License-Identifier: Apache-2.0
 ***********************************************************************/

import type { Locator, Page } from '@playwright/test';
import test, { expect as playExpect } from '@playwright/test';

import { SettingsPage } from './settings-page';

export class ResourcesPage extends SettingsPage {
  readonly heading: Locator;
  readonly featuredProviderResources: Locator;

  constructor(page: Page) {
    super(page, 'Resources');
    this.heading = this.header.getByRole('heading', { name: 'Title' }).and(this.header.getByText('Resources'));
    this.featuredProviderResources = this.content.getByRole('region', { name: 'Featured Provider Resources' });
  }

  public async resourceCardIsVisible(resourceCardAriaLabel: string): Promise<boolean> {
    return (await this.resourceCardLocatorGenerator(resourceCardAriaLabel).count()) > 0;
  }

  public async goToCreateNewResourcePage(resourceCardAriaLabel: string, newResourceName = ''): Promise<void> {
    if (!(await this.resourceCardIsVisible(resourceCardAriaLabel))) {
      throw new Error(`Resource card with label "${resourceCardAriaLabel}" is not available`);
    }

    const buttonName = newResourceName || resourceCardAriaLabel;
    await this.resourceCardLocatorGenerator(resourceCardAriaLabel)
      .getByRole('button', { name: `Create new ${buttonName}` })
      .click();
  }

  public getProviderUpdateButton(providerCardLabel: string): Locator {
    return this.resourceCardLocatorGenerator(providerCardLabel).getByRole('button', { name: /Update to/ });
  }

  public async updateProvider(providerCardLabel: string, timeout = 60_000): Promise<this> {
    return test.step(`Update provider ${providerCardLabel} from Resources page`, async () => {
      const providerCard = this.resourceCardLocatorGenerator(providerCardLabel);
      await playExpect(providerCard).toBeVisible({ timeout: 10_000 });

      const updateButton = this.getProviderUpdateButton(providerCardLabel);
      await playExpect(updateButton).toBeVisible({ timeout: 10_000 });
      await playExpect(updateButton).toBeEnabled();
      await updateButton.click();
      await playExpect(updateButton).toBeHidden({ timeout });
      return this;
    });
  }

  private resourceCardLocatorGenerator(resourceCardAriaLabel: string): Locator {
    return this.content.getByRole('region', { name: resourceCardAriaLabel, exact: true });
  }
}
